// @ts-nocheck
import React, { Component } from 'react';
import { sortBy, uniq, without } from 'lodash';
import DelayedTextInput from './delayed-text-input';
import SelectTwoColumnControls from './select-two-column-controls';
import SelectTwoColumnItem from './select-two-column-item';
import SimpleSelect from './simple-select';
import { MembershipItem } from '../permission-members/index';
import css from './css';

interface Props {
  type: string;
  info?: string | JSX.Element;
  items: Item[];
  roles?: ReadonlyArray<Item>;
  titles?: string[];
  height: number;
  invert?: boolean;
  selectedValues: string[];
  preselectedObjects?: Record<string, string[]>;
  onChange: (selectedValues: string[]) => void;
}

export interface Item {
  value: number | string;
  label: string;
  email?: string;
  // eslint-disable-next-line camelcase
  is_owner?: boolean;
  // eslint-disable-next-line camelcase
  role_name?: string;
  // eslint-disable-next-line camelcase
  is_disabled?: boolean;
  // eslint-disable-next-line camelcase
  from_group?: boolean;
}

interface State {
  role: Item;
  highlightedValues: string[];
  search: string;
  searchRole: boolean;
}

export function toOrderedItems(
  objects: MembershipItem[], labelKey, valueKey, emailKey, roleKey, ownerKey, fromGroupKey,
) {
  const items = objects.map((item) => ({
    label: item[labelKey],
    value: item[valueKey],
    email: item[emailKey],
    role_name: item[roleKey],
    from_group: item[fromGroupKey],
    is_disabled: item[ownerKey],
  }));

  return sortBy(items, 'label');
}

const filterRemoveValues = (item: Item) => !((item.is_disabled ?? false) || (item.role_name?.toUpperCase() === 'OWNER') || (item.from_group ?? false));

export default class SelectTwoColumn extends Component<Props, State> {
  static defaultProps = {
    height: 290,
  };

  constructor(props) {
    super(props);
    const { roles } = this.props;
    this.state = {
      role: roles?.[0],
      highlightedValues: [],
      search: '',
      searchRole: false,
    };
  }

  componentDidUpdate(prevProps, prevState) {
    const { role } = this.state;

    if (role && (role.label !== prevState.role?.label)) {
      this.setState({ searchRole: true });
    }
  }

  handleToggleSelected = (value: string) => {
    const { highlightedValues } = this.state;
    if (highlightedValues.indexOf(value) !== -1) {
      this.setState({ highlightedValues: without(highlightedValues, value) });
    } else {
      this.setState({ highlightedValues: uniq([...highlightedValues, value]) });
    }
  };

  handleAddValues = () => {
    const selectedValues = uniq([...this.props.selectedValues, ...this.state.highlightedValues]);

    this.setState({
      highlightedValues: [],
    }, () => {
      this.props.onChange(selectedValues);
    });
  };

  handleRemoveValues = () => {
    const selectedValues = uniq(without(this.props.selectedValues, ...this.state.highlightedValues));

    this.setState({
      highlightedValues: [],
    }, () => {
      this.props.onChange(selectedValues);
    });
  };

  handleAddAllValues = (items: Item[]) => {
    const selectedValues = uniq([...items.map((item) => item.value), ...this.props.selectedValues]);

    this.setState({
      highlightedValues: [],
    }, () => {
      this.props.onChange(selectedValues as string[]);
    });
  };

  handleRemoveAllValues = (items: Item[]) => {
    let filteredItems = [];
    if (items.length) {
      filteredItems = items.filter(filterRemoveValues);
    }
    const itemValues = filteredItems.map((item) => item.value);
    const { selectedValues: propsSelectedVals } = this.props;
    const selectedElements = without(propsSelectedVals, ...itemValues);
    const selectedValues = uniq(selectedElements);

    this.setState({
      highlightedValues: [],
    }, () => {
      this.props.onChange(selectedValues as string[]);
    });
  };

  handleInputFocus = () => {
    this.setState({ highlightedValues: [] });
  };

  handlePermissionSearch = (search: string) => {
    const { roles } = this.props;
    this.setState({
      searchRole: false,
      search,
      role: roles?.[0],
    });
  };

  handleRoleChange = (role: Item) => this.setState({ role, search: '' });

  get filteredItems() {
    const { items } = this.props;
    const { search } = this.state;
    if (search.length === 0) {
      return items;
    }

    return items.filter(
      (option) => option.label.toLowerCase().indexOf(search.toLowerCase()) !== -1,
    );
  }

  get filteredUnselectedItems() {
    return this.filteredItems.filter((item) => !this.isSelected(item));
  }

  get filteredSelectedItems() {
    return this.filteredItems.filter((item) => this.isSelected(item));
  }

  get filteredItemsByRole() {
    const { role } = this.state;
    const { items } = this.props;
    if (role.label === 'All Roles') {
      return items;
    }
    return items.filter((item) => item.role_name?.indexOf(role.label) !== -1);
  }

  get filteredUnselectedItemsByRole() {
    return this.filteredItemsByRole.filter((item) => !this.isSelected(item));
  }

  get filteredSelectedItemsByRole() {
    return this.filteredItemsByRole.filter((item) => this.isSelected(item));
  }

  get preselectedValues() {
    const { preselectedObjects } = this.props;
    return preselectedObjects && Object.keys(preselectedObjects);
  }

  isSelected(item: Item) {
    const { selectedValues } = this.props;
    return !!(selectedValues.includes(item.value) || this.preselectedValues?.includes(item.value));
  }

  renderItems(options: Item[]) {
    return options.map((option, i, { length }) => {
      const { highlightedValues } = this.state;
      const isActive = highlightedValues.includes(option.value);
      const isPreselected = this.preselectedValues?.includes(option.value);
      return (
        <SelectTwoColumnItem
          key={i}
          label={option.label}
          value={option.value as string}
          email={option.email || ((isPreselected || option.from_group) && 'Access via Group')}
          role={option.role_name}
          last={i + 1 === length && length > 9}
          selected={isActive || isPreselected}
          disabled={option.is_owner || option.is_disabled || isPreselected || option.from_group}
          isGroupManaged={option.from_group}
          type={this.props.type}
          onSelect={this.handleToggleSelected}
        />
      );
    });
  }

  render() {
    const { searchRole } = this.state;
    const unselectedItems = !searchRole
      ? this.filteredUnselectedItems : this.filteredUnselectedItemsByRole;
    const selectedItems = !searchRole
      ? this.filteredSelectedItems : this.filteredSelectedItemsByRole;

    const {
      invert, info, roles, height, titles,
    } = this.props;
    return (
      <>
        {typeof info === 'string' ? (
          <p style={{ marginBottom: 20 }}>{info}</p>
        ) : (
          <>{info}</>
        )}
        <DelayedTextInput
          onValueChange={this.handlePermissionSearch}
          className={customStyles.searchBox}
          containerStyle={customStyles.inputContainer}
          placeholder="Search"
          value={this.state.search}
          searchIcon
          focus={this.handleInputFocus}
          delay={0}
        />
        {roles && (
          <div className={customStyles.roles}>
            <SimpleSelect
              options={roles}
              isClearable={false}
              onChange={this.handleRoleChange}
            />
          </div>
        )}
        <div
          style={{ height, flexDirection: invert ? 'row-reverse' : 'row' }}
          className={customStyles.wrapper}
        >
          <div className={customStyles.multipleContainer} data-testid="unselected-items-column">
            <p className={customStyles.label}>
              {titles[0]}
              {' '}
              (
              {unselectedItems.length}
              )
            </p>
            <div className={customStyles.multipleBox}>
              {unselectedItems.length === 0
                && (
                  <p className={customStyles.emptyBox}>
                    No results
                  </p>
                )}
              {this.renderItems(unselectedItems)}
            </div>
          </div>
          <div className={customStyles.controlsContainer}>
            <SelectTwoColumnControls
              invert={invert}
              add={this.handleAddValues}
              remove={this.handleRemoveValues}
              addAll={() => this.handleAddAllValues(unselectedItems)}
              removeAll={() => this.handleRemoveAllValues(selectedItems)}
            />
          </div>
          <div className={customStyles.multipleContainer} data-testid="selected-items-column">
            <p className={customStyles.label}>
              {titles[1]}
              {' '}
              (
              {selectedItems.length}
              )
            </p>
            <div className={customStyles.multipleBox}>
              {selectedItems.length === 0
                && (
                  <p className={customStyles.emptyBox}>
                    No results
                  </p>
                )}
              {this.renderItems(selectedItems)}
            </div>
          </div>
        </div>
      </>
    );
  }
}

const customStyles = css({
  wrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 10,
  },

  multipleContainer: {
    width: '44%',
    display: 'flex',
    flexDirection: 'column',
  },

  label: {
    color: '#b5b5b5',
    userSelect: 'none',
    margin: '0 0 9px',
  },

  emptyBox: {
    color: '#b5b5b5',
    textAlign: 'center',
    margin: 0,
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },

  multipleBox: {
    overflow: 'auto',
    flex: 1,
    height: '88%',
    border: '1px solid #ddd',
    position: 'relative',
    borderRadius: 4,
  },

  controlsContainer: {
    width: '8%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },

  inputContainer: {
    width: '100%',
    marginBottom: 25,
  },

  searchBox: {
    width: '100%',
  },

  roles: {
    width: '35%',
    marginBottom: 25,
    marginTop: -20,
  },

  batch: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
});
